import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Paper, Typography, Box, TextField, Button, MenuItem, Select, FormControl, InputLabel, Alert, IconButton, InputAdornment, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { IMaskInput } from 'react-imask';
import { useApplicationContext } from 'ApplicationContext';
import { FileTypeLicenseDto, FileTypeVariantsDto } from 'apiClient/Api';
import { useParams, useNavigate, useSearchParams } from 'react-router'; // Import useParams for URL parameters

/**
 * LicenseRequest component
 * 
 * This component renders a form for requesting licenses with a description field and license code input.
 * The input is formatted with separators (-) and automatically jumps to the next block.
 */
export const LicenseRequest: React.FC = () => {
    const { t } = useTranslation();
    const { supportId } = useParams<{ supportId: string }>(); // Get supportCode from URL
    const [searchParams] = useSearchParams();
    const [licenseCode, setLicenseCode] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [modus, setModus] = useState<string | null>(null);
    const [key, setKey] = useState<string | null>(null);
    const [licenseGeneratorVersions, setLicenseGeneratorVersions] =useState<FileTypeLicenseDto[]>([]);
    const [selectedProduct, setSelectedProduct] = useState<string | undefined>(undefined);
    const [variants, setVariants] = useState<FileTypeVariantsDto[]>([]);
    const [urlParamsApplied, setUrlParamsApplied] = useState<boolean>(false);
    const [callbackUrl, setCallbackUrl] = useState<string | null>(null);
    const [deleteCallbackDialogOpen, setDeleteCallbackDialogOpen] = useState<boolean>(false);
    const context = useApplicationContext();
    const navigate = useNavigate();
    const descriptionRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        context.api.service.serviceGetLicenseVersions().then((data) => {
            setLicenseGeneratorVersions(data.data);
        }).catch((error) => {
            console.error(error);
        });
    },[context.api.service]);

    // Read and apply URL parameters once when products are loaded
    useEffect(() => {
        // Only apply URL parameters once the products are loaded and not already applied
        if (licenseGeneratorVersions.length === 0 || urlParamsApplied) return;
        
        const productIdParam = searchParams.get('productId');
        const licenseCodeParam = searchParams.get('licenseCode');
        const variantParam = searchParams.get('variant');
        const descriptionParam = searchParams.get('description');
        const cbParam = searchParams.get('cb');
        
        if (cbParam) {
            setCallbackUrl(cbParam);
        }
        
        if (productIdParam) {
            setSelectedProduct(productIdParam);
        }
        
        if (licenseCodeParam) {
            // Format the license code with dashes
            const formattedCode = licenseCodeParam.replace(/[^A-Fa-f0-9]/g, '').toUpperCase();
            if (formattedCode.length <= 12) {
                const formatted = formattedCode.match(/.{1,4}/g)?.join('-') || formattedCode;
                setLicenseCode(formatted);
            }
        }
        
        if (descriptionParam) {
            setDescription(descriptionParam);
        }
        
        if (variantParam) {
            // Set variant after product is set - convert empty string if needed
            setTimeout(() => setModus(variantParam === '' ? null : variantParam), 100);
        }
        
        // Focus description field if other fields are pre-filled
        if ((productIdParam || licenseCodeParam) && descriptionRef.current) {
            setTimeout(() => descriptionRef.current?.focus(), 200);
        }
        
        setUrlParamsApplied(true);
    }, [licenseGeneratorVersions, urlParamsApplied, searchParams]);

    const loadVariants = useCallback(
        async (fileTypeId: string) => {
            try {
                const response = await context.api.fileType.fileTypeGetFileTypePublic(fileTypeId);
                setVariants(response.data.variants ?? []);
            } catch (error) {
                setVariants([]);
                context.apiError(error);
            }
        },
        [context.api.fileType, context.apiError],
    );

    useEffect(() => {
        if (selectedProduct) {
            loadVariants(selectedProduct);
        } else {
            setVariants([]);
            setModus(null);
        }
    }, [selectedProduct, loadVariants]);

    /**
     * Fetches license data based on the provided license code, description, and modus.
     * 
     * @param licenseCode - The license code
     */
    const getLicenseData = useCallback(async (licenseCode: string, resetCode: boolean = false) => {

        if(selectedProduct){

            context.api.service.serviceRequestLicense(
                {
                    description: description,
                    supportCodeId: supportId!,
                    hostId: licenseCode,
                    fileTypeId: selectedProduct,
                    tag: modus,
                    callbackUrl: callbackUrl
                }
            ).then((data) => {
                //setKey(data.data);
                if (resetCode) {
                    setLicenseCode('');
                    setDescription('');
                    descriptionRef.current?.focus();
                } else {
                    navigate(`/licenses/${supportId}`);
                }
            }).catch((error) => {
                setKey(null);
            });
        }

    }, [selectedProduct, modus, description]);

    /**
     * Handles changes to the license code input.
     * 
     * @param value - The new license code value
     */
    const handleLicenseCodeChange = (value: string) => {
        setLicenseCode(value.toUpperCase());
        setKey(null); // Reset the license key when the input changes
    };

    /**
     * Determines if the order button should be enabled.
     * 
     * @returns {boolean} - True if the button should be enabled, false otherwise
     */
    const isOrderButtonEnabled = () => {
        // Remove dashes and check if we have exactly 12 hex characters
        const cleanCode = licenseCode.replace(/-/g, '');
        return description !== '' && selectedProduct !== undefined && cleanCode.length === 12;
    };

    return (
        <Box width={1}>
            <Paper elevation={3} sx={{ padding: 2 }}>
                <Typography variant="h6" gutterBottom>
                    {t('licenseGenerator.title')}
                </Typography>
                
                {/* Debug: Show Product IDs if showIds=true in URL */}
                {searchParams.get('showIds') === 'true' && (
                    <Box sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                            Debug: Available Product IDs
                        </Typography>
                        {licenseGeneratorVersions.map((product) => (
                            <Box key={product.id} sx={{ fontFamily: 'monospace', fontSize: '0.85rem', mb: 0.5 }}>
                                <strong>{product.name}:</strong> {product.id}
                            </Box>
                        ))}
                        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #ddd' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                Current State:
                            </Typography>
                            <Box sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                selectedProduct: {selectedProduct || 'undefined'}
                            </Box>
                            <Box sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                modus (variant): {modus || 'null/empty'}
                            </Box>
                            <Box sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                licenseCode: {licenseCode || 'empty'}
                            </Box>
                            <Box sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                urlParamsApplied: {urlParamsApplied ? 'true' : 'false'}
                            </Box>
                        </Box>
                    </Box>
                )}
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap:2, paddingBottom: 2 }}>


                <FormControl sx={{ flexGrow: 1 }}>
                        <InputLabel>{t('licenseGenerator.product')}</InputLabel>
                        <Select
                            value={selectedProduct || ''}
                            onChange={(e) => setSelectedProduct(e.target.value)}
                            label={t('licenseGenerator.product')}
                        >
                            <MenuItem value="">-</MenuItem>
                            {licenseGeneratorVersions.map((product) => (
                                <MenuItem key={product.id} value={product.id}>
                                    {product.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
             
                {variants.length >= 0 && selectedProduct && (
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, paddingBottom: 2 }}>
                        <FormControl sx={{ flexGrow: 1 }}>
                            <InputLabel shrink>{t('licenseGenerator.variant')}</InputLabel>
                            <Select
                                value={modus === null ? '' : modus}
                                notched
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setModus(val === '' ? null : val);
                                }}
                                label={t('licenseGenerator.variant')}
                                displayEmpty
                                renderValue={(selected) => {
                                    if (!selected || selected === '') {
                                        return <span style={{ color: '#9e9e9e' }}>{t('licenseGenerator.default')}</span>;
                                    }
                                    // Find the variant to display its name
                                    const variant = variants.find(v => v.tag === selected);
                                    if (variant) {
                                        return variant.name && variant.name.trim() !== '' 
                                            ? variant.name 
                                            : (variant.tag || t('licenseGenerator.default'));
                                    }
                                    return selected;
                                }}
                            >
                                {/* Always show Standard option if no variant with empty tag exists OR if it's currently selected */}
                                {(!variants.some(v => v.tag === '' || v.tag === null) || modus === null) && (
                                    <MenuItem value="">{t('licenseGenerator.default')}</MenuItem>
                                )}
                                
                                {variants.map((variant) => {
                                    const variantTag = variant.tag === null || variant.tag === '' ? '' : variant.tag;
                                    const displayName = variant.name && variant.name.trim() !== '' 
                                        ? variant.name 
                                        : (variant.tag || t('licenseGenerator.default'));
                                    return (
                                        <MenuItem key={variant.tag || 'default'} value={variantTag}>
                                            {displayName}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                    </Box>
                )}

                    <TextField
                        label={t('licenseGenerator.description')}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        required
                        inputRef={descriptionRef}
                    />

                    {callbackUrl && (
                        <TextField
                            label={t('licenseGenerator.callbackUrl')}
                            value={atob(callbackUrl)}
                            disabled
                            fullWidth
                            helperText={t('licenseGenerator.callbackUrlHelperText')}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setDeleteCallbackDialogOpen(true)}
                                            edge="end"
                                            color="error"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                    )}

                    <Alert severity="info" sx={{ mt: 1 }}>
                        {t('licenseGenerator.approvalInfo')}
                    </Alert>


                <IMaskInput
                        mask="0000-0000-0000"
                        definitions={{
                            '0': /[A-Fa-f0-9]/
                        }}
                        value={licenseCode}
                        onAccept={(value: any) => handleLicenseCodeChange(value)}
                        overwrite
                        placeholder={t('licenseGenerator.code')}
                        style={{ flexGrow: 1, padding: '16.5px 14px', fontSize: '1rem', border: '1px solid rgba(0, 0, 0, 0.23)', borderRadius: '4px' }}
                    />
               </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Button 
                        variant="contained" 
                        onClick={() => getLicenseData(licenseCode)} 
                        disabled={!isOrderButtonEnabled()}
                    >
                        {t('licenseGenerator.addToOrder')}
                    </Button>
                    <Button 
                        variant="contained" 
                        color="primary" 
                        onClick={() => getLicenseData(licenseCode, true)} 
                        disabled={!isOrderButtonEnabled()}
                    >
                        {t('licenseGenerator.addToOrderAndAddAdditional')}
                    </Button>
                    <Button 
                        variant="contained" 
                        color="secondary" 
                        onClick={() => navigate(`/licenses/${supportId}`)}
                    >
                        {t('licenseGenerator.backToOverview')}
                    </Button>
                    <Typography variant="body2" color="textSecondary">
                        {t('licenseGenerator.confirmOrderAllText')}
                    </Typography>
                </Box>
            </Paper>

            {/* Confirmation Dialog for Removing Callback URL */}
            <Dialog
                open={deleteCallbackDialogOpen}
                onClose={() => setDeleteCallbackDialogOpen(false)}
            >
                <DialogTitle>{t('licenseGenerator.removeCallbackTitle')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('licenseGenerator.removeCallbackMessage')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteCallbackDialogOpen(false)} color="primary">
                        {t('licenseGenerator.cancel')}
                    </Button>
                    <Button 
                        onClick={() => {
                            setCallbackUrl(null);
                            setDeleteCallbackDialogOpen(false);
                        }} 
                        color="error"
                        variant="contained"
                    >
                        {t('licenseGenerator.remove')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

